import { ApolloClient } from '@apollo/client';
import axios from 'axios';
import { v4 as uuid } from 'uuid';

import { finleyAssert, finleyBuffer } from '@finley-technologies/common';
import { PyXlsx2Csv2Extractor } from '@finley-technologies/tape-sanitizer';

import { presignedPostUpload } from 'packages/files/ui/utils';
import { GET_PYXLSX2CSV2_UPLOAD_SIGNED_URL } from 'packages/tapeBuilder/requests/tapeBuilder.gql';
import {
  GetExtractPyXlsx2Csv2DocumentGQLType,
  GetExtractPyXlsx2Csv2QueryGQLType,
  GetExtractPyXlsx2Csv2QueryVariablesGQLType,
} from 'packages/tapeBuilder/requests/tapeBuilder.gql.generated';

export const getPyXlsx2Csv2Extractor = ({
  apolloClient,
  pipelineDefinitionId,
  nodeId,
  getInputNodeIds,
  extensionMap,
  setQAStatus,
}: {
  apolloClient: ApolloClient<object>;
  pipelineDefinitionId: string;
  nodeId: string;
  getInputNodeIds: (nodeId: string) => string[];
  extensionMap: Map<string, string>;
  setQAStatus: (status: string) => Promise<void>;
}): PyXlsx2Csv2Extractor => {
  return async ({ excelBuffer, extractionParams }) => {
    const inputNodeIds = getInputNodeIds(nodeId);

    finleyAssert(
      inputNodeIds.length === 1,
      'getPyXlsx2Csv2Extractor node can only have 1 input',
    );
    const inputNodeId = inputNodeIds[0];

    const extension = extensionMap.get(inputNodeId);

    finleyAssert(
      extension === 'xlsx' || extension === 'xlsb',
      `getPyXlsx2Csv2Extractor only supports xlsx or xlsb extension, but got [ext:${extension}]`,
    );

    // TypeScript 6.0: Use helper to get a properly typed ArrayBuffer
    const blob = new Blob([finleyBuffer.toArrayBuffer(excelBuffer.buffer)], {
      type: excelBuffer.extension,
    });
    const file = new File([blob], 'pyXlsx2Csv2inputFile', {
      type: excelBuffer.extension,
    });

    await setQAStatus('Xlsx extraction: Uploading file.');

    // get presignedUrl and upload to s3
    const { queryResponse } = await presignedPostUpload<
      {
        pipelineDefinitionId: string;
        extension: string;
        requestId: string;
      },
      { s3Bucket: string; s3Path: string }
    >({
      client: apolloClient,
      file,
      query: GET_PYXLSX2CSV2_UPLOAD_SIGNED_URL,
      queryVariables: {
        pipelineDefinitionId,
        extension,
        requestId: uuid(),
      },
    });

    await setQAStatus('Xlsx extraction: Starting extraction.');

    // long poll for csv completion signed download url
    // may need to revisit this requests are timing out
    const { data: extractedCsvSignedDownload } = await apolloClient.query<
      GetExtractPyXlsx2Csv2QueryGQLType,
      GetExtractPyXlsx2Csv2QueryVariablesGQLType
    >({
      query: GetExtractPyXlsx2Csv2DocumentGQLType,
      variables: {
        pipelineDefinitionId,
        xlsxS3Bucket: queryResponse.s3Bucket,
        xlsxS3Key: queryResponse.s3Path,
        extractionParams,
      },
    });

    await setQAStatus('Xlsx extraction: Downloading extracted csv.');

    // download the extract csv to client memory
    const res = await axios({
      url: extractedCsvSignedDownload.extractPyXlsx2Csv2,
      method: 'GET',
      responseType: 'arraybuffer',
    });

    const buffer = finleyBuffer.fromArrayBuffer(res.data);

    await setQAStatus('');

    return {
      isValid: true,
      data: { outputBuffer: buffer },
      supplementals: [],
    };
  };
};
